import { useEffect, useState } from "react";
import { Landmark, Search, Mail, Phone, Globe, Plus, Check, Loader2, RotateCcw, Info, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full bg-[#09090D] border border-zinc-800 focus:border-amber-500/60 focus:outline-none rounded-xl px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 transition-colors duration-300";

const NOTES_IMPORT =
  "Importé de l'annuaire officiel service-public.fr. Demander le service animation / culture / fêtes.";

export default function AnnuaireTab() {
  const [mode, setMode] = useState("commune");
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dep, setDep] = useState("");
  const [depLabel, setDepLabel] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [existingEmails, setExistingEmails] = useState(new Set());
  const [existingNoms, setExistingNoms] = useState(new Set());
  const [addedKeys, setAddedKeys] = useState(new Set());
  const [scans, setScans] = useState({});

  const refreshExisting = async () => {
    try {
      const { data } = await api.get("/prospects");
      setExistingEmails(new Set(data.map((p) => (p.email || "").toLowerCase()).filter(Boolean)));
      setExistingNoms(new Set(data.map((p) => p.nom.toLowerCase())));
    } catch {}
  };

  useEffect(() => {
    refreshExisting();
  }, []);

  useEffect(() => {
    if (mode === "departement" || selected) return;
    const q = query.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/annuaire/communes", { params: { q } });
        setSuggestions(data);
      } catch {
        setSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, selected, mode]);

  const selectCommune = async (c) => {
    setSelected(c);
    setQuery(`${c.nom} (${c.departement})`);
    setSuggestions([]);
    setLoading(true);
    setResults(null);
    try {
      const endpoint = mode === "lieux" ? "/annuaire/lieux-live" : "/annuaire/mairies";
      const { data } = await api.get(endpoint, { params: { code_insee: c.code } });
      setResults(data);
      if (data.length === 0) {
        toast.info(mode === "lieux" ? "Aucun bar/salle référencé dans un rayon de 4 km" : "Aucune collectivité trouvée pour cette commune");
      } else if (mode === "lieux") {
        toast.info(`${data.length} bars & salles dans un rayon de 4 km — source OpenStreetMap`);
      }
    } catch (err) {
      toast.error("Recherche impossible", { description: formatApiErrorDetail(err.response?.data?.detail) });
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const searchDepartement = async () => {
    const d = dep.trim().toUpperCase();
    if (!d) return;
    setLoading(true);
    setResults(null);
    setSuggestions([]);
    try {
      const { data } = await api.get("/annuaire/departement", { params: { dep: d } });
      setDepLabel(d);
      setResults(data);
      if (data.length === 0) toast.info("Aucune mairie trouvée pour ce département");
    } catch (err) {
      toast.error("Recherche impossible", { description: formatApiErrorDetail(err.response?.data?.detail) });
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setSelected(null);
    setQuery("");
    setResults(null);
    setSuggestions([]);
    setDepLabel("");
  };

  const switchMode = (m) => {
    setMode(m);
    reset();
  };

  const displayName = (r) =>
    r.nom.startsWith("Mairie de") || r.nom.startsWith("Mairie —")
      ? r.nom
      : r.type === "Mairie" && selected
        ? `Mairie de ${selected.nom}`
        : r.nom.replace(" - ", " — ");

  const itemKey = (r) => (r.email || r.nom).toLowerCase();

  const inCrm = (r) =>
    addedKeys.has(itemKey(r)) ||
    (r.email && existingEmails.has(r.email.toLowerCase())) ||
    existingNoms.has(displayName(r).toLowerCase());

  const buildPayload = (r) => ({
    nom: displayName(r),
    type: r.type,
    departement: r.departement || selected?.departement || "",
    email: r.email || "",
    telephone: r.telephone || "",
    site_web: r.site_web || "",
    statut: "À contacter",
    notes:
      r.source === "osm"
        ? "Trouvé via OpenStreetMap — vérifier que le lieu programme de la musique live."
        : NOTES_IMPORT,
  });

  const addToCrm = async (r) => {
    try {
      const { data } = await api.post("/prospects", buildPayload(r));
      setAddedKeys((s) => new Set(s).add(itemKey(r)));
      toast.success(`${data.nom} ajouté à la prospection`);
    } catch (err) {
      toast.error("Ajout impossible", { description: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  const importAll = async () => {
    const items = (results || []).filter((r) => !inCrm(r)).map(buildPayload);
    if (items.length === 0) {
      toast.info("Toutes ces collectivités sont déjà dans le CRM");
      return;
    }
    setBulkLoading(true);
    try {
      const { data } = await api.post("/prospects/bulk", { items });
      toast.success(`${data.added} collectivité(s) importée(s)`, {
        description: data.skipped ? `${data.skipped} déjà présente(s), ignorée(s)` : undefined,
      });
      await refreshExisting();
      setAddedKeys(new Set([...addedKeys, ...(results || []).map(itemKey)]));
    } catch (err) {
      toast.error("Import impossible", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setBulkLoading(false);
    }
  };

  const scanSite = async (r, i) => {
    setScans((s) => ({ ...s, [i]: { loading: true } }));
    try {
      const { data } = await api.get("/annuaire/emails-site", { params: { url: r.site_web } });
      setScans((s) => ({ ...s, [i]: { loading: false, results: data } }));
    } catch (err) {
      setScans((s) => ({
        ...s,
        [i]: { loading: false, error: formatApiErrorDetail(err.response?.data?.detail) },
      }));
    }
  };

  const addSiteEmail = async (r, s) => {
    try {
      const payload = {
        ...buildPayload(r),
        nom: `${displayName(r)} — ${s.culture ? "service culture" : "contact site"}`,
        email: s.email,
        notes: `Trouvé sur ${s.source} — vérifier qu'il s'agit bien du service culture / animation.`,
      };
      const { data } = await api.post("/prospects", payload);
      toast.success(`${data.nom} ajouté à la prospection`);
      refreshExisting();
    } catch (err) {
      toast.error("Ajout impossible", { description: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  const remaining = (results || []).filter((r) => !inCrm(r)).length;

  return (
    <div data-testid="annuaire-tool">
      <div className="rounded-2xl border border-amber-500/20 bg-[#161622] p-6 mb-6">
        <div className="flex items-start gap-3 mb-5">
          <Landmark size={22} className="text-amber-400 shrink-0 mt-0.5" />
          <div>
            <h2 className="font-display font-bold uppercase tracking-wide text-lg">
              Annuaire officiel des collectivités
            </h2>
            <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
              Coordonnées publiques des mairies (annuaire de l'État service-public.fr) et des bars
              &amp; salles de concert (OpenStreetMap), importables en un clic dans votre prospection.
            </p>
          </div>
        </div>

        <div className="flex gap-2 mb-5">
          <button
            data-testid="annuaire-mode-commune"
            onClick={() => switchMode("commune")}
            className={`text-xs font-bold uppercase tracking-wider rounded-full px-4 py-2 border transition-colors duration-300 ${
              mode === "commune"
                ? "bg-amber-500 text-white border-amber-500"
                : "border-zinc-700 text-zinc-400 hover:text-amber-400 hover:border-amber-500/40"
            }`}
          >
            Par commune
          </button>
          <button
            data-testid="annuaire-mode-departement"
            onClick={() => switchMode("departement")}
            className={`text-xs font-bold uppercase tracking-wider rounded-full px-4 py-2 border transition-colors duration-300 ${
              mode === "departement"
                ? "bg-amber-500 text-white border-amber-500"
                : "border-zinc-700 text-zinc-400 hover:text-amber-400 hover:border-amber-500/40"
            }`}
          >
            Tout un département
          </button>
          <button
            data-testid="annuaire-mode-lieux"
            onClick={() => switchMode("lieux")}
            className={`text-xs font-bold uppercase tracking-wider rounded-full px-4 py-2 border transition-colors duration-300 ${
              mode === "lieux"
                ? "bg-amber-500 text-white border-amber-500"
                : "border-zinc-700 text-zinc-400 hover:text-amber-400 hover:border-amber-500/40"
            }`}
          >
            Bars & salles
          </button>
        </div>

        {mode !== "departement" ? (
          <div className="relative max-w-xl">
            <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="annuaire-search-input"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                if (selected) {
                  setSelected(null);
                  setResults(null);
                }
              }}
              placeholder="Nom de la commune… (ex. : Vienne, Annecy, Bourg-en-Bresse)"
              className={`${inputCls} pl-11`}
            />
            {suggestions.length > 0 && (
              <div
                data-testid="annuaire-suggestions"
                className="absolute z-20 left-0 right-0 mt-2 rounded-xl border border-zinc-800 bg-[#09090D] overflow-hidden shadow-xl shadow-black/50"
              >
                {suggestions.map((c) => (
                  <button
                    key={c.code}
                    data-testid={`annuaire-suggestion-${c.code}`}
                    onClick={() => selectCommune(c)}
                    className="w-full text-left px-4 py-3 text-sm text-zinc-200 hover:bg-amber-500/10 hover:text-amber-300 transition-colors flex items-center justify-between"
                  >
                    <span className="font-semibold">{c.nom}</span>
                    <span className="text-xs font-jbmono text-zinc-500">
                      {c.departement} · {c.code_postal}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3 max-w-xl">
            <input
              data-testid="annuaire-departement-input"
              value={dep}
              onChange={(e) => setDep(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && searchDepartement()}
              placeholder="N° de département (ex. : 38, 73, 2A)"
              className={`${inputCls} w-64`}
            />
            <button
              data-testid="annuaire-departement-search-button"
              onClick={searchDepartement}
              disabled={loading}
              className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-white text-sm font-bold uppercase tracking-wider rounded-full px-5 py-3 transition-colors duration-300"
            >
              {loading ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
              Lister les mairies
            </button>
          </div>
        )}

        {(selected || results) && (
          <button
            data-testid="annuaire-reset-button"
            onClick={reset}
            className="mt-4 flex items-center gap-2 text-xs font-jbmono uppercase tracking-wider text-zinc-500 hover:text-amber-400 transition-colors"
          >
            <RotateCcw size={12} />
            Nouvelle recherche
          </button>
        )}
      </div>

      {loading && (
        <div className="flex flex-col items-center gap-3 py-16">
          <Loader2 className="animate-spin text-amber-500" size={28} data-testid="annuaire-loading" />
          {mode === "departement" && (
            <p className="text-xs text-zinc-500">Interrogation de l'annuaire pour tout le département…</p>
          )}
        </div>
      )}

      {results && !loading && (
        <div data-testid="annuaire-results" className="space-y-3">
          <div className="flex flex-wrap items-center gap-4">
            <p className="text-xs font-jbmono uppercase tracking-widest text-zinc-500">
              {results.length} résultat{results.length > 1 ? "s" : ""}
              {selected ? ` — ${selected.nom}${mode === "lieux" ? " et alentours (4 km)" : ""}` : depLabel ? ` — département ${depLabel}` : ""}
            </p>
            {remaining > 0 && (
              <button
                data-testid="annuaire-import-all-button"
                onClick={importAll}
                disabled={bulkLoading}
                className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-white text-xs font-bold uppercase tracking-wider rounded-full px-4 py-2 transition-colors duration-300"
              >
                {bulkLoading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                Tout importer ({remaining})
              </button>
            )}
          </div>
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            {results.map((r, i) => (
              <div
                key={i}
                data-testid={`annuaire-result-${i}`}
                className="rounded-2xl border border-zinc-800 bg-[#161622] p-5 hover:border-amber-500/30 transition-colors duration-300"
              >
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-display font-bold uppercase tracking-wide">{displayName(r)}</h3>
                    <span className="text-[10px] font-jbmono uppercase tracking-widest border border-amber-500/30 text-amber-400 rounded-full px-2.5 py-0.5">
                      {r.type}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-2 text-xs text-zinc-400">
                    {r.email ? (
                      <a href={`mailto:${r.email}`} className="flex items-center gap-1.5 hover:text-amber-400 transition-colors">
                        <Mail size={12} /> {r.email}
                      </a>
                    ) : (
                      <span className="flex items-center gap-1.5 text-zinc-600">
                        <Mail size={12} /> Email non publié
                      </span>
                    )}
                    {r.telephone && (
                      <a href={`tel:${r.telephone}`} className="flex items-center gap-1.5 hover:text-amber-400 transition-colors">
                        <Phone size={12} /> {r.telephone}
                      </a>
                    )}
                    {r.site_web && (
                      <a
                        href={r.site_web}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 hover:text-amber-400 transition-colors"
                      >
                        <Globe size={12} /> Site web
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  {r.site_web && (
                    <button
                      data-testid={`annuaire-scan-${i}`}
                      onClick={() => scanSite(r, i)}
                      disabled={scans[i]?.loading}
                      className="flex items-center gap-2 border border-zinc-700 hover:border-amber-500/40 text-zinc-300 hover:text-amber-400 text-xs font-bold uppercase tracking-wider rounded-full px-4 py-2 transition-colors duration-300"
                    >
                      {scans[i]?.loading ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />}
                      Emails culture
                    </button>
                  )}
                  <button
                    data-testid={`annuaire-add-${i}`}
                    onClick={() => addToCrm(r)}
                    disabled={inCrm(r)}
                    className={`flex items-center gap-2 text-sm font-bold uppercase tracking-wider rounded-full px-5 py-2.5 transition-colors duration-300 ${
                      inCrm(r)
                        ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 cursor-default"
                        : "bg-amber-500 hover:bg-amber-400 text-white"
                    }`}
                  >
                    {inCrm(r) ? <Check size={15} /> : <Plus size={15} />}
                    {inCrm(r) ? "Dans le CRM" : "Ajouter au CRM"}
                  </button>
                </div>
              </div>
              {(scans[i]?.results || scans[i]?.error) && (
                <div data-testid={`annuaire-scan-results-${i}`} className="mt-4 border-t border-zinc-800 pt-3 space-y-1.5">
                  {scans[i]?.error && <p className="text-xs text-red-400">{scans[i].error}</p>}
                  {scans[i]?.results?.length === 0 && (
                    <p className="text-xs text-zinc-500 leading-relaxed">
                      Aucun email publié sur ce site — la mairie utilise un formulaire de contact.
                      Le plus efficace : écrire à l'email d'accueil en demandant le service
                      culture / animation / fêtes.
                    </p>
                  )}
                  {scans[i]?.results?.map((s) => (
                    <div key={s.email} className="flex items-center gap-2 text-xs flex-wrap">
                      <Mail size={11} className={s.culture ? "text-amber-400" : "text-zinc-500"} />
                      <span className="text-zinc-300">{s.email}</span>
                      {s.culture && (
                        <span className="text-[9px] font-jbmono uppercase tracking-widest text-amber-400 border border-amber-500/30 rounded-full px-2 py-0.5">
                          culture
                        </span>
                      )}
                      {s.officiel && (
                        <span className="text-[9px] font-jbmono uppercase tracking-widest text-sky-400 border border-sky-500/30 rounded-full px-2 py-0.5">
                          site officiel
                        </span>
                      )}
                      <button
                        data-testid={`annuaire-scan-add-${i}-${s.email}`}
                        onClick={() => addSiteEmail(r, s)}
                        className="ml-auto text-amber-400 hover:text-amber-300 font-bold uppercase text-[10px] tracking-wider transition-colors"
                      >
                        Ajouter
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            ))}
          </div>
        </div>
      )}

      {!results && !loading && (
        <div
          data-testid="annuaire-info-banner"
          className="rounded-2xl border border-dashed border-zinc-800 p-6 flex items-start gap-3"
        >
          <Info size={16} className="text-zinc-500 shrink-0 mt-0.5" />
          <p className="text-sm text-zinc-500 leading-relaxed">
            Les coordonnées affichées sont des données publiques B2B de l'annuaire de
            l'administration (service-public.fr). L'email affiché est souvent celui de l'accueil de
            la mairie : demandez à être mis en relation avec le service animation, culture ou fêtes
            et cérémonies. Proposez toujours un moyen simple de ne plus être contacté.
          </p>
        </div>
      )}
    </div>
  );
}
